<template>
  <v-card class="contract-shell" elevation="0">
    <!-- Barra Superior Minimalista y Elegante -->
    <div class="contract-top-bar px-5 py-3 bg-white border-bottom d-flex align-center justify-space-between flex-wrap">
      <div class="d-flex align-center">
        <v-avatar size="38" color="primary lighten-5" class="mr-3">
          <v-icon color="primary" size="22">mdi-file-document-edit-outline</v-icon>
        </v-avatar>
        <div>
          <div class="d-flex align-center flex-wrap">
            <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-4 mb-0 mr-2">
              Creación / Edición de Contrato
            </h3>
            <v-chip x-small color="primary lighten-5" class="primary--text font-weight-bold">
              {{ isExtension ? 'Ampliación' : isRenovation ? 'Renovación' : isReadOnlyMode ? 'Solo Lectura' :
                'Expediente' }}
            </v-chip>
          </div>
          <div class="text-caption grey--text text--darken-2" v-if="activeWorkerDni">
            <span class="mx-1">•</span>
            <span class="font-weight-medium">DNI:</span> {{ activeWorkerDni }}
          </div>
        </div>
      </div>

      <!-- Acciones de Cabecera -->
      <div class="d-flex align-center gap-2 mt-2 mt-sm-0 flex-wrap">
        <!-- Switch/Flag para mantener datos del contrato al guardar datos personales -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="d-none d-sm-flex align-center mr-2 px-3 py-1 rounded-pill"
              style="background: #f1f5f9; border: 1px solid #e2e8f0;"
            >
              <v-icon small :color="preserveContractData ? 'primary' : 'grey'" class="mr-1">
                mdi-shield-refresh-outline
              </v-icon>
              <span class="text-caption font-weight-medium grey--text text--darken-3 mr-2">
                Mantener datos contrato
              </span>
              <v-switch
                v-model="preserveContractData"
                color="primary"
                hide-details
                dense
                class="ma-0 pa-0"
              />
            </div>
          </template>
          <span>Conserva los datos laborales ya ingresados cuando guardes los datos personales</span>
        </v-tooltip>

        <v-btn v-if="currentPrsID" color="primary" outlined small class="rounded-lg text-none font-weight-bold"
          @click="openNewContract">
          <v-icon small left>mdi-plus</v-icon> Nuevo Contrato
        </v-btn>

        <v-btn v-if="!isReadOnlyMode" color="primary" elevation="0" small
          class="rounded-lg text-none font-weight-bold px-4" :disabled="saving" :loading="saving"
          @click="saveCurrentSection()">
          <v-icon small left>mdi-content-save</v-icon>
          Guardar Cambios
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" color="grey darken-2" @click="$emit('close')">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </template>
          <span>Cerrar Formulario</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Pestañas de Navegación de Contrato -->
    <div class="contract-tabs-bar px-4 pt-2 pb-0 bg-white border-bottom d-flex justify-center">
      <v-tabs v-model="currentTab" centered color="primary" class="contract-tabs">
        <v-tab href="#tab-personal" v-if="!isExtension && !isRenovation" class="font-weight-bold text-none">
          <v-icon small left class="mr-1">mdi-account-outline</v-icon>
          Datos Personales
        </v-tab>
        <v-tab href="#tab-laboral" class="font-weight-bold text-none">
          <v-icon small left class="mr-1">mdi-briefcase-outline</v-icon>
          Datos Laborales
        </v-tab>
        <v-tab href="#tab-sctr" v-if="isSCTR" class="font-weight-bold text-none">
          <v-icon small left class="mr-1">mdi-shield-check-outline</v-icon>
          Datos Asegurados (SCTR)
        </v-tab>
        <v-tab href="#tab-historial" @click="loadContracts" class="font-weight-bold text-none">
          <v-icon small left class="mr-1">mdi-history</v-icon>
          Historial de Contratos
        </v-tab>
      </v-tabs>
    </div>

    <v-row no-gutters>
      <v-col cols="12">
        <!-- Contenido de las pestañas -->
        <keep-alive>
          <v-tabs-items v-model="currentTab">
            <!-- PESTAÑA 1: PERSONA -->
            <v-tab-item :value="'tab-personal'" v-if="!isExtension && !isRenovation">
              <v-card class="pa-4 pa-sm-6 contract-tab-panel" elevation="0"
                style="background-color: #f8fafc; min-height: 85vh;">
                <v-row justify="center">
                  <v-col cols="12" md="11" lg="11" xl="10">
                    <PersonalForm ref="personalFormRef" :visibleTitle="false" :datawork="workerData"
                      @personSaved="onPersonSaved" @onSearch="onPersonSearch" />
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- PESTAÑA 2: LABORAL -->
            <v-tab-item :value="'tab-laboral'">
              <v-card class="pa-4 pa-sm-6 contract-tab-panel" elevation="0"
                style="background-color: #f8fafc; min-height: 85vh;">
                <v-row justify="center">
                  <v-col cols="12" md="11" lg="11" xl="10">
                    <ContractLaborForm ref="contractLaborRef" :datawork="contractFormData" :isNewWorker="isNewWorker"
                      :extension="isExtension" :renovation="isRenovation" :isViewOnly="isReadOnlyMode"
                      @sctrChanged="isSCTR = $event" @categoryChanged="onCategoryChanged" />
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- PESTAÑA 3: SCTR -->
            <v-tab-item :value="'tab-sctr'" v-if="isSCTR">
              <v-card class="pa-4 pa-sm-6 contract-tab-panel" elevation="0"
                style="background-color: #f8fafc; min-height: 85vh;">
                <v-row justify="center">
                  <v-col cols="12" md="11" lg="11" xl="10">
                    <ContractSctrForm ref="contractSctrRef" :datawork="contractFormData" :agtID="selectedAgtID || 0" />
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- PESTAÑA 5: HISTORIAL / TIMELINE -->
            <v-tab-item :value="'tab-historial'">
              <v-card class="pa-4 pa-sm-6 contract-tab-panel" elevation="0"
                style="background-color: #f8fafc; min-height: 85vh;">
                <v-row justify="center">
                  <v-col cols="12" md="11" lg="11" xl="10">
                    <ContractTimeline :contracts="contracts" :loading="loadingContracts" :selectedAgtID="selectedAgtID"
                      @edit="openEditContract" @renovate="openRenovateContract" @refresh="loadContracts" />
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </keep-alive>
      </v-col>
    </v-row>

    <!-- Dialog Digitalización DNI -->
    <dni-photo-capture-modal v-model="showDniModal" :dni="activeWorkerDni" :worker-name="activeWorkerName"
      :position-name="activePositionName" :agt-id="activeAgtId"
      :operator-name="($fun && $fun.getUserName && $fun.getUserName()) || 'OPERADOR RRHH'" />
  </v-card>
</template>

<script>
import PersonalForm from "@/components/HumanResource/Contracts/PersonalData/PersonalForm.vue";
import ContractLaborForm from "@/components/HumanResource/Contracts/ContractLaborForm.vue";
import ContractSctrForm from "@/components/HumanResource/Contracts/ContractSctrForm.vue";
import ContractTimeline from "@/components/HumanResource/Contracts/ContractTimeline.vue";
import DniPhotoCaptureModal from "@/views/HumanResource/Contract/DniPhotoCaptureModal.vue";

import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import _sWorkertService from "@/services/HumanResource/WorkerService.js";

export default {
  name: "PersonalDataDemo",

  props: {
    contractContext: { type: Object, default: null },
    objEdit: { type: Object, default: null },
    extension: { type: Boolean, default: false },
    renovation: { type: Boolean, default: false },
    isViewOnly: { type: Boolean, default: false },
  },

  components: {
    PersonalForm,
    ContractLaborForm,
    ContractSctrForm,
    ContractTimeline,
    DniPhotoCaptureModal,
  },

  data() {
    return {
      currentTab: "tab-personal",
      // Estado de la persona
      currentPrsID: null,
      currentNtpID: null,
      currentWkrID: null,
      currentDocNumber: "",
      isNewWorker: false,
      lastKnownPstID: 0,

      // Datos del trabajador (banco, seguros, etc.)
      workerData: {
        WkrID: null,
        BankAccountsCE: [],
        HmnPension: [],
        HmnHealth: [],
      },

      // Contratos
      contracts: [],
      loadingContracts: false,
      selectedAgtID: null,

      // Formulario de contrato
      editingContract: null,
      isExtension: false,
      isRenovation: false,
      isSCTR: false,
      isModalidadFormativa: false,
      contractFormData: {},
      saving: false,
      showDniModal: false,
      // Flag para preservar datos laborales ingresados al guardar datos personales
      preserveContractData: true,
    };
  },

  computed: {
    activeWorkerDni() {
      return (
        this.currentDocNumber ||
        (this.contractFormData && (this.contractFormData.PrsDocumentNumber || this.contractFormData.DNI || this.contractFormData.Dni)) ||
        (this.workerData && (this.workerData.PrsDocumentNumber || this.workerData.DNI || this.workerData.Dni)) ||
        (this.objEdit && (this.objEdit.PrsDocumentNumber || this.objEdit.DNI || this.objEdit.Dni)) ||
        ""
      );
    },
    activeWorkerName() {
      return (
        (this.contractFormData && this.contractFormData.NtpFullName) ||
        (this.workerData && this.workerData.NtpFullName) ||
        (this.objEdit && (this.objEdit.NtpFullName || this.objEdit.FullName)) ||
        ""
      );
    },
    activePositionName() {
      return (
        (this.contractFormData && this.contractFormData.PstName) ||
        (this.objEdit && (this.objEdit.PstName || this.objEdit.PositionName)) ||
        ""
      );
    },
    activeAgtId() {
      return (
        this.selectedAgtID ||
        (this.contractFormData && this.contractFormData.AgtID) ||
        (this.objEdit && this.objEdit.AgtID) ||
        0
      );
    },
    isContractInactive() {
      if (this.isExtension || this.isRenovation) return false;
      if (this.selectedAgtID && this.editingContract) {
        return this.editingContract.AgtStatus == 2;
      }
      if (
        this.contractFormData &&
        this.contractFormData.AgtID > 0 &&
        this.contractFormData.AgtStatus == 2
      ) {
        return true;
      }
      return false;
    },
    isReadOnlyMode() {
      return this.isViewOnly || this.isContractInactive;
    },
  },

  watch: {
    contractContext: {
      immediate: true,
      deep: true,
      handler(context) {
        if (context) {
          this.applyContractContext(context);
        }
      },
    },
    objEdit: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.openFromObjEdit(val);
        }
      },
    },
  },

  mounted() {
    if (this.contractContext) {
      this.applyContractContext(this.contractContext);
    }
  },

  methods: {
    saveCurrentSection() {
      if (this.isReadOnlyMode) {
        this.$fun.alert(
          "No se puede editar ni guardar un contrato inactivo.",
          "warning",
        );
        return;
      }
      // Delegar según la pestaña activa
      if (this.currentTab === "tab-personal") {
        const form = this.$refs.personalFormRef;
        if (form && typeof form.save === "function") {
          form.save();
        }
      } else {
        // En tabs laborales, asegurar, etc. disparamos el guardado de contrato general
        this.saveContract();
      }
    },

    hasContractDataInProgress() {
      const laborRef = this.$refs.contractLaborRef;
      const f = laborRef && laborRef.form ? laborRef.form : this.contractFormData;
      if (!f) return false;
      return !!(
        f.PstID ||
        f.AgtSalary ||
        f.AreID ||
        f.CceID ||
        f.BslID ||
        f.CmpID ||
        f.ReqID ||
        f.BatAccountNumber ||
        f.BatAccountNumber_CTS
      );
    },

    async onPersonSaved(person) {
      if (!person) return;

      // 1. Actualizar identificadores actuales de la persona
      this.currentPrsID = person.PrsID || person.GenPerson?.PrsID || this.currentPrsID;
      this.currentNtpID = person.NtpID || person.GenPerson?.NtpID || this.currentNtpID;
      this.currentWkrID = person.WkrID || person.GenPerson?.WkrID || this.currentWkrID;
      const documentNumber =
        person.PrsDocumentNumber ||
        person.GenPerson?.PrsDocumentNumber ||
        person.DNI ||
        this.currentDocNumber;
      this.currentDocNumber = documentNumber;

      // 2. Cargar historial y datos complementarios del trabajador si existe WkrID
      if (this.currentWkrID) {
        await this.loadWorkerData(this.currentWkrID, person);
        await this.loadContracts();
      }

      const laborRef = this.$refs.contractLaborRef;
      const hasLaborForm = laborRef && laborRef.form;
      const hasDataEntered = this.hasContractDataInProgress();

      // 3. Si estamos en modo edición, ampliación, renovación, O si el flag preserveContractData está activo
      // y ya existen datos laborales en progreso o contexto de requerimiento:
      if (
        this.editingContract ||
        this.selectedAgtID ||
        this.isExtension ||
        this.isRenovation ||
        (this.preserveContractData && (hasDataEntered || this.contractContext))
      ) {
        if (hasLaborForm) {
          // Sincronizar IDs sin limpiar formulario
          if (this.currentWkrID) laborRef.form.WkrID = this.currentWkrID;
          if (this.currentNtpID) laborRef.form.NtpID = this.currentNtpID;
          if (this.currentPrsID) laborRef.form.PrsID = this.currentPrsID;

          if (this.currentWkrID && typeof laborRef.loadWorkerFinancialData === "function") {
            await laborRef.loadWorkerFinancialData(this.currentWkrID);
          }

          // Mantener sincronizado contractFormData sin disparar hydrate/reset
          Object.assign(this.contractFormData, laborRef.form, {
            WkrID: this.currentWkrID,
            NtpID: this.currentNtpID,
            PrsID: this.currentPrsID,
          });
        }

        this.currentTab = "tab-laboral";
        this.$fun.alert(
          "Datos personales guardados. Se mantuvieron intactos los datos del contrato.",
          "success",
        );
        return;
      }

      // 4. Si no había datos ingresados ni flag activo, flujo regular de carga limpia
      await this.loadPersonContext(person, false);
      this.currentTab = "tab-laboral";
      this.$fun.alert(
        "Datos personales guardados. Por favor, complete los datos laborales.",
        "success",
      );
    },

    async onPersonSearch(person) {
      if (!person || !person.PrsID) return;
      const documentNumber =
        person.PrsDocumentNumber ||
        person.GenPerson?.PrsDocumentNumber ||
        person.DNI ||
        "";

      if (
        this.editingContract ||
        this.selectedAgtID ||
        this.isExtension ||
        this.isRenovation ||
        (this.preserveContractData &&
          this.hasContractDataInProgress() &&
          (!this.currentDocNumber || this.currentDocNumber === documentNumber))
      ) {
        this.currentPrsID = person.PrsID || person.GenPerson?.PrsID || 0;
        this.currentNtpID = person.NtpID || person.GenPerson?.NtpID || 0;
        this.currentWkrID = person.WkrID || person.GenPerson?.WkrID || 0;
        this.currentDocNumber = documentNumber;

        if (this.currentWkrID && this.$refs.contractLaborRef?.form) {
          this.$refs.contractLaborRef.form.WkrID = this.currentWkrID;
          this.$refs.contractLaborRef.form.NtpID = this.currentNtpID;
        }
        return;
      }
      await this.loadPersonContext(person);
    },

    async applyContractContext(context) {
      // console.log("PersonalDataDemo.applyContractContext", context);
      if (!context) return;
      const prsDoc = context.PrsDocumentNumber || context.DNI || "";
      let resolvedContext = { ...context };

      if (prsDoc) {
        try {
          const evalDate = context.MevDate || context.EvaluationDate || null;
          const res = await _sCttoService.contractAutomationByDocument(prsDoc, evalDate);
          if (res?.status === 200 && res.data) {
            const data = Array.isArray(res.data) ? res.data[0] : res.data;
            resolvedContext = { ...context, ...data };
            if (context.TypeCivilStatus) {
              resolvedContext.TypeCivilStatus = context.TypeCivilStatus;
            }
            if (data.AgtChildrenQuantity != null && Number(data.AgtChildrenQuantity) > 0) {
              resolvedContext.AgtChildrenQuantity = Number(data.AgtChildrenQuantity);
            } else if (context.AgtChildrenQuantity !== undefined && context.AgtChildrenQuantity !== null) {
              resolvedContext.AgtChildrenQuantity = Number(context.AgtChildrenQuantity);
            }
          }
        } catch (e) {
          console.warn("Error resolviendo automatización de contrato:", e);
        }
      }

      this.$emit("contractContextReady", {
        sourceLabel: resolvedContext.SourceLabel || null,
        reqID: resolvedContext.ReqID || null,
        areID: resolvedContext.AreID || null,
        cceID: resolvedContext.CceID || null,
        pstID: resolvedContext.PstID || null,
      });

      this.contractFormData = {
        ...this.contractFormData,
        ...resolvedContext,
        ReqID: resolvedContext.ReqID || null,
      };

      if (resolvedContext.TypeCategory == 5) {
        this.isModalidadFormativa = true;
      }
      if (resolvedContext.AgtIsSCTR) {
        this.isSCTR = true;
      }

      this.$nextTick(async () => {
        if (prsDoc && this.$refs.personalFormRef) {
          if (typeof this.$refs.personalFormRef.searchByDocument === "function") {
            await this.$refs.personalFormRef.searchByDocument(prsDoc);
          }
        }
        if (this.$refs.contractLaborRef) {
          this.$refs.contractLaborRef.resetForm(this.contractFormData);
        }
      });
    },

    async openFromObjEdit(val) {
      if (!val) return;
      // console.log("PersonalDataDemo.openFromObjEdit", val);

      const contractObj = val.AgreementCE || val;
      const wkrID = val.WkrID || contractObj.WkrID || 0;
      const ntpID = val.NtpID || contractObj.NtpID || 0;
      const prsID = val.PrsID || val.GenPerson?.PrsID || contractObj.PrsID || 0;
      const docNumber =
        val.PrsDocumentNumber || contractObj.PrsDocumentNumber || val.DNI || "";

      this.currentWkrID = wkrID;
      this.currentNtpID = ntpID;
      this.currentPrsID = prsID;
      this.currentDocNumber = docNumber;

      if (this.currentWkrID) {
        await this.loadWorkerData(this.currentWkrID, val);
        await this.loadContracts();
      }

      if (this.extension) {
        this.editingContract = contractObj;
        this.isExtension = true;
        this.isRenovation = false;
        this.isSCTR = !!contractObj.AgtIsSCTR;
        this.isModalidadFormativa = contractObj.TypeCategory == 5;
        this.selectedAgtID = contractObj.AgtID;
        const rawEndDate =
          contractObj.AgtEndDate ||
          val.AgtEndDate ||
          contractObj._prevAgtEndDate ||
          val._prevAgtEndDate ||
          null;
        const formattedEndDate = rawEndDate
          ? String(rawEndDate).substring(0, 10)
          : null;
        this.contractFormData = {
          ...contractObj,
          AgtEndDate: formattedEndDate,
          _prevAgtEndDate: formattedEndDate,
          initialModificationScope: "MOD_LABORAL",
        };
        this.currentTab = "tab-laboral";
        return;
      }

      if (this.renovation) {
        this.openRenovateContract(contractObj);
        return;
      }

      if (this.currentWkrID || this.currentPrsID) {
        if (this.isViewOnly || (contractObj && contractObj.AgtStatus == 2)) {
          this.editingContract = contractObj;
          this.isExtension = false;
          this.isRenovation = false;
          this.isSCTR = !!contractObj.AgtIsSCTR;
          this.isModalidadFormativa = contractObj.TypeCategory == 5;
          this.selectedAgtID = contractObj.AgtID;
          this.contractFormData = {
            ...contractObj,
            initialModificationScope: "MOD_VIEW",
          };
          this.currentTab = "tab-laboral";
        } else {
          this.openEditContract(contractObj, "MOD_ALL");
        }
      }

      // Cargar datos personales del trabajador solo si no es ampliación ni renovación
      if (docNumber && this.$refs.personalFormRef) {
        if (typeof this.$refs.personalFormRef.searchByDocument === "function") {
          await this.$refs.personalFormRef.searchByDocument(docNumber);
        }
      }
    },

    async loadPersonContext(person, preserveLabor = false) {
      this.currentPrsID = person.PrsID || person.GenPerson?.PrsID || 0;
      this.currentNtpID = person.NtpID || person.GenPerson?.NtpID || 0;
      this.currentWkrID = person.WkrID || person.GenPerson?.WkrID || 0;
      const documentNumber =
        person.PrsDocumentNumber ||
        person.GenPerson?.PrsDocumentNumber ||
        person.DNI ||
        "";
      this.currentDocNumber = documentNumber;

      this.lastKnownPstID = 0;
      let activeContract = null;
      let probation = null;

      // Cargar contratos de fondo para lógica de prueba y auto-carga
      if (this.currentWkrID) {
        await this.loadContracts();

        if (this.contracts.length === 0) {
          // Tiene WkrID pero sin contratos = nuevo
          probation = 3;
        } else {
          activeContract = this.contracts.find((c) => c.AgtStatus == 1);
          if (activeContract) {
            // Contrato activo = contrato continuo, sin periodo de prueba
            probation = 0;
          } else {
            const lastContract = this.contracts[0];
            console.log("las contract", lastContract);
            if (lastContract.AgtEndDate) {
              const threeYearsAgo = new Date();
              threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
              if (new Date(lastContract.AgtEndDate) < threeYearsAgo) {
                // Último contrato terminó hace más de 3 años = nuevo
                probation = 3;
              } else {
                // Reintegro (menos de 3 años) = sin prueba
                probation = 0;
              }
            } else {
              // Contrato sin fecha fin (indeterminado cesado) = reintegro
              probation = 0;
            }
          }
        }
      } else {
        // Sin WkrID = trabajador completamente nuevo
        probation = 3;
      }

      this.isNewWorker = probation === 3;

      // Al buscar una persona, SIEMPRE resetear el estado de edición a un nuevo contrato
      this.editingContract = null;
      this.isExtension = false;
      this.isRenovation = false;
      this.isSCTR = false;
      this.isModalidadFormativa = false;
      this.selectedAgtID = null;

      // Consultar automatización / requerimiento por DNI solo si NO tiene un contrato activo
      let automationData = null;
      if (documentNumber && !activeContract) {
        try {
          const evalDate =
            this.contractContext?.MevDate ||
            this.contractContext?.EvaluationDate ||
            null;
          const resAuto = await _sCttoService.contractAutomationByDocument(
            documentNumber,
            evalDate,
          );
          if (resAuto?.status === 200 && resAuto.data) {
            const data = Array.isArray(resAuto.data)
              ? resAuto.data[0]
              : resAuto.data;
            if (
              data &&
              (data.ReqID ||
                data.AreID ||
                data.PstID ||
                data.HasRequirementData ||
                data.HasMassiveSource ||
                data.HasProfessionalSource ||
                data.TypeLaborRegime != null ||
                data.TypePayroll != null ||
                data.TypeContract != null)
            ) {
              automationData = data;
            }
          }
          console.log("datos de contractAutomationByDocument", automationData);
        } catch (e) {
          console.warn(
            "Error buscando automatización/requerimiento por DNI:",
            e,
          );
        }
      } else if (activeContract) {
        this.$fun.alert(
          `El colaborador ya cuenta con un contrato ACTIVO (#${activeContract.AgtID}). No se puede asociar a un requerimiento de ingreso mientras mantenga un contrato activo. Use las opciones de Renovación o Ampliación.`,
          "warning",
        );
      }

      const todayStr = new Date().toISOString().split("T")[0];
      const lastContract =
        this.contracts && this.contracts.length > 0 ? this.contracts[0] : null;

      if (automationData) {
        // Caso 1: Hay un Requerimiento (automatización)
        this.contractFormData = {
          _isNew: true,
          WkrID: this.currentWkrID,
          NtpID: this.currentNtpID,
          AgtStatus: 0,
          AgtMaximunDay: true,
          AgtMonthsProbation: probation,
          AgtBeginDate: todayStr,
          AgtAdmissionDate: todayStr,
          EstID: 1,
          ...automationData,
        };
        this.isModalidadFormativa = automationData.TypeCategory == 5;
        this.isSCTR = !!automationData.AgtIsSCTR;
      } else if (lastContract) {
        // Caso 2: No hay Requerimiento, pero sí historial de contratos -> Copiar del último contrato
        const copy = JSON.parse(JSON.stringify(lastContract));
        copy._isNew = true;
        copy.AgtID = 0;
        copy.WkrID = this.currentWkrID;
        copy.NtpID = this.currentNtpID;
        copy.AgtStatus = 0;
        copy.AgtMaximunDay = true;
        copy.AgtMonthsProbation = probation;
        copy.AgtBeginDate = todayStr;
        copy.AgtAdmissionDate = copy.AgtAdmissionDate || todayStr;
        copy.AgtEndDate = null;
        copy.AgtTerminationDate = null;
        copy.AgtRenovation = 0;
        copy.ReqID = null;
        copy.EstID = copy.EstID || 1;
        copy.AgtSignatureBase64 = null;
        copy.AgtSignatureDateTime = null;
        copy.AgtFingerPrintBase64 = null;
        copy.AgtFingerPrintDateTime = null;
        copy.AgtSignedPdfPath = null;
        copy.AgtIsSigned = 0;
        copy.TypeTermination = null;
        if (copy.TypeWorker == null && copy.TypePosition) {
          copy.TypeWorker = copy.TypePosition;
        }

        if (copy.AgreementMovemts && copy.AgreementMovemts.length > 0) {
          copy.AgreementMovemts.forEach((m) => {
            m.AmsID = 0;
            m.AmsBeginDate = todayStr;
            m.AmsAdmissionDate = todayStr;
            m.AmsEndDate = null;
            m.AmsTerminationDate = null;
            m.AmsStartDateMovement = todayStr;
            m.AmsEndDateMovement = null;
            m.AmsRenovation = 0;
            m.TypeTermination = null;
            m.AgtStatus = 0;
          });
        }

        this.isModalidadFormativa = copy.TypeCategory == 5;
        this.isSCTR = !!copy.AgtIsSCTR;
        this.contractFormData = copy;
      } else {
        // Caso 3: Ni requerimiento ni contratos previos -> Formulario vacío/limpio
        this.contractFormData = {
          _isNew: true,
          WkrID: this.currentWkrID,
          NtpID: this.currentNtpID,
          AgtStatus: 0,
          AgtMaximunDay: true,
          AgtMonthsProbation: probation,
          AgtBeginDate: todayStr,
          AgtAdmissionDate: todayStr,
          EstID: 1,
        };
        this.isModalidadFormativa = false;
        this.isSCTR = false;
      }

      if (this.contractFormData) {
        if (this.contractContext?.TypeCivilStatus) {
          this.contractFormData.TypeCivilStatus = this.contractContext.TypeCivilStatus;
        }
        if (
          automationData?.AgtChildrenQuantity != null &&
          Number(automationData.AgtChildrenQuantity) > 0
        ) {
          this.contractFormData.AgtChildrenQuantity = Number(automationData.AgtChildrenQuantity);
        } else if (
          this.contractContext?.AgtChildrenQuantity !== undefined &&
          this.contractContext?.AgtChildrenQuantity !== null
        ) {
          this.contractFormData.AgtChildrenQuantity = Number(this.contractContext.AgtChildrenQuantity);
        }
      }

      console.log("contrato formado fusionado", this.contractFormData);

      if ((preserveLabor || this.preserveContractData) && this.hasContractDataInProgress()) {
        const laborRef = this.$refs.contractLaborRef;
        if (laborRef?.form) {
          if (this.currentWkrID) laborRef.form.WkrID = this.currentWkrID;
          if (this.currentNtpID) laborRef.form.NtpID = this.currentNtpID;
          if (this.currentPrsID) laborRef.form.PrsID = this.currentPrsID;
          if (this.currentWkrID && typeof laborRef.loadWorkerFinancialData === "function") {
            await laborRef.loadWorkerFinancialData(this.currentWkrID);
          }
          Object.assign(this.contractFormData, laborRef.form);
        }
        return;
      }

      this.$nextTick(() => {
        if (this.$refs.contractLaborRef) {
          this.$refs.contractLaborRef.resetForm(this.contractFormData);
        }
      });
    },

    // ────────────────────────────────────────────────────────
    // WORKER DATA
    // ────────────────────────────────────────────────────────
    async loadWorkerData(wkrID, person = null) {
      try {
        const res = await _sCttoService.workerPerson(
          wkrID,
          0,
          this.$fun.getUserID(),
        );
        const personAccounts =
          person?.BankAccounts || person?.GenPerson?.BankAccounts || [];
        const personPensions =
          person?.Pensions || person?.GenPerson?.Pensions || [];
        const personHealths =
          person?.Healths || person?.GenPerson?.Healths || [];

        if (res?.status === 200 && res.data) {
          this.workerData = {
            WkrID: wkrID,
            BankAccountsCE:
              res.data.BankAccountsCE && res.data.BankAccountsCE.length
                ? res.data.BankAccountsCE
                : personAccounts,
            HmnPension:
              res.data.PensionCE && res.data.PensionCE.length
                ? res.data.PensionCE
                : personPensions,
            HmnHealth:
              res.data.HealthCE && res.data.HealthCE.length
                ? res.data.HealthCE
                : personHealths,
          };
        } else {
          this.workerData = {
            WkrID: wkrID,
            BankAccountsCE: personAccounts,
            HmnPension: personPensions,
            HmnHealth: personHealths,
          };
        }
      } catch (e) {
        console.error("Error al cargar datos del trabajador:", e);
      }
    },

    // ────────────────────────────────────────────────────────
    // CONTRATOS
    // ────────────────────────────────────────────────────────
    async loadContracts() {
      if (!this.currentWkrID) {
        this.contracts = [];
        return;
      }

      this.loadingContracts = true;
      try {
        const res = await _sCttoService.HmnContractByWkrID(
          this.currentWkrID,
          this.$fun.getUserID(),
        );
        if (res?.status === 200) {
          // console.log("contratos historial", res.data);
          const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
          this.contracts = raw;
        }
      } catch (e) {
        console.error("Error cargando contratos:", e);
      } finally {
        this.loadingContracts = false;
      }
    },

    async openNewContract() {
      // Intentamos copiar del contrato que se estaba editando/viendo o del más reciente del historial
      const baseContract =
        this.editingContract ||
        (this.contracts && this.contracts.length > 0
          ? this.contracts[0]
          : null);

      this.editingContract = null;
      this.isExtension = false;
      this.isRenovation = false;
      this.selectedAgtID = null;

      if (baseContract) {
        // Hacemos una copia profunda del contrato base para no alterar el original
        const copy = JSON.parse(JSON.stringify(baseContract));
        const todayStr = new Date().toISOString().split("T")[0];

        // Borramos el AgtID, fechas y datos de cese/firma, seteando inicio/ingreso a hoy
        copy.AgtID = 0;
        copy.AgtBeginDate = todayStr;
        copy.AgtAdmissionDate = todayStr;
        copy.AgtEndDate = null;
        copy.AgtTerminationDate = null;
        copy.AgtStatus = 0;
        copy.AgtRenovation = 0;
        copy.AgtSignatureBase64 = null;
        copy.AgtSignatureDateTime = null;
        copy.AgtFingerPrintBase64 = null;
        copy.AgtFingerPrintDateTime = null;
        copy.AgtSignedPdfPath = null;
        copy.AgtIsSigned = 0;
        copy.TypeTermination = null;

        // Limpiar fechas y datos específicos en movimientos si existieran, seteando inicio a hoy
        if (copy.AgreementMovemts && copy.AgreementMovemts.length > 0) {
          copy.AgreementMovemts.forEach((m) => {
            m.AmsID = 0;
            m.AmsBeginDate = todayStr;
            m.AmsAdmissionDate = todayStr;
            m.AmsEndDate = null;
            m.AmsTerminationDate = null;
            m.AmsStartDateMovement = todayStr;
            m.AmsEndDateMovement = null;
            m.AmsRenovation = 0;
            m.TypeTermination = null;
            m.AgtStatus = 0;
          });
        }

        copy._isNew = true;
        this.isSCTR = !!copy.AgtIsSCTR;
        this.isModalidadFormativa = copy.TypeCategory == 5;
        this.contractFormData = copy;
        this.$fun.alert(
          "Formulario cargado copiando los datos del contrato anterior",
          "success",
        );
      } else {
        // Trigger a clean form if no previous contract exists
        // console.log("entrando a opennewcontract Else ->");
        const todayStr = new Date().toISOString().split("T")[0];
        this.isSCTR = false;
        this.isModalidadFormativa = false;
        this.contractFormData = {
          _isNew: true, // flag to force hydrate reset
          WkrID: this.currentWkrID,
          NtpID: this.currentNtpID,
          AgtStatus: 0,
          AgtMaximunDay: true,
          AgtMonthsProbation: this.isNewWorker ? 3 : null,
          AgtBeginDate: todayStr,
          AgtAdmissionDate: todayStr,
        };
        this.$fun.alert("Formulario limpio para un nuevo contrato", "info");
      }

      this.currentTab = "tab-laboral";
    },

    openEditContract(contract, scope = "MOD_LABORAL") {
      if (!contract || contract.AgtStatus == 2) {
        this.$fun.alert("No se puede editar un contrato de baja.", "warning");
        return;
      }
      this.editingContract = contract;
      this.isExtension = false;
      this.isRenovation = false;
      this.isSCTR = !!contract.AgtIsSCTR;
      this.isModalidadFormativa = contract.TypeCategory == 5;
      this.selectedAgtID = contract.AgtID;
      this.contractFormData = { ...contract, initialModificationScope: scope };
      this.currentTab = "tab-laboral";
      this.$nextTick(() => {
        if (
          this.$refs.contractLaborRef &&
          typeof this.$refs.contractLaborRef.setModificationScope === "function"
        ) {
          this.$refs.contractLaborRef.setModificationScope(scope);
        }
      });
    },

    isIndeterminateContract(contract) {
      if (!contract) return false;
      if (!contract.AgtEndDate) return true;
      if (contract.TypeSituation == 1) return true;
      const typeName = (contract.TypeContractName || "").toLowerCase();
      if (typeName.includes("indeterminado") || typeName.includes("indefinido"))
        return true;
      return false;
    },

    openRenovateContract(contract) {
      if (this.isIndeterminateContract(contract)) {
        this.$fun.alert(
          "Un contrato a plazo indeterminado/indefinido no puede ser renovado.",
          "warning",
        );
        return;
      }
      this.editingContract = contract;
      this.isExtension = false;
      this.isRenovation = true;
      this.isSCTR = !!contract.AgtIsSCTR;
      this.isModalidadFormativa = contract.TypeCategory == 5;
      const prevEndStr = contract.AgtEndDate
        ? String(contract.AgtEndDate).substring(0, 10)
        : null;
      let nextBeginStr = new Date().toISOString().substring(0, 10);
      if (prevEndStr) {
        const prevEnd = new Date(prevEndStr + "T00:00:00");
        prevEnd.setDate(prevEnd.getDate() + 1);
        nextBeginStr = prevEnd.toISOString().substring(0, 10);
      }

      this.contractFormData = {
        ...contract,
        AgtID: 0,
        AgtRenewedFromID: contract.AgtID,
        AgtAdmissionDate: contract.AgtAdmissionDate || contract.AgtBeginDate,
        AgtBeginDate: nextBeginStr,
        AgtEndDate: nextBeginStr,
        _prevAgtEndDate: prevEndStr,
        AgtRenovation: 0,
        ReqID: null,
        SourceLabel: null,
        HasRequirementData: false,
        initialModificationScope: "MOD_LABORAL",
        TypeWorker: contract.TypeWorker || contract.TypePosition || 4,
        TypeLaborRegime: contract.TypeLaborRegime || 3,
        TypeOccupationalCategory:
          contract.TypeOccupationalCategory || contract.TypeWorker || 4,
      };
      this.currentTab = "tab-laboral";
    },

    closeContractForm() {
      this.editingContract = null;
      this.selectedAgtID = null;
      this.currentTab = "tab-historial";
      this.$emit("close");
    },

    onSctrChanged(val) {
      this.isSCTR = !!val;
      if (this.isSCTR && this.contractTab === "tab-labor") {
        // no avanzamos automáticamente, el usuario presiona "Siguiente"
      }
    },

    onCategoryChanged(val) {
      this.isModalidadFormativa = val == 5;
    },

    advanceToSctr() {
      const labor = this.$refs.contractLaborRef?.getData();
      if (!labor) return;

      // Validar carrera si es modalidad formativa
      if (this.isModalidadFormativa) {
        const majors = this.$refs.personalFormRef?.getMajors?.() || [];
        if (!majors.some((m) => m.SecStatus == 1)) {
          this.$fun.alert(
            "Para Modalidad Formativa debe registrar una carrera académica en Datos Personales.",
            "warning",
          );
          return;
        }
      }

      this.contractTab = "tab-sctr";
    },

    advanceToBank() {
      const sctr = this.$refs.contractSctrRef?.getData();
      if (!sctr) return;
      this.contractTab = "tab-bank";
    },

    async saveContract() {
      if (this.isReadOnlyMode) {
        this.$fun.alert(
          "No se puede editar ni guardar un contrato inactivo.",
          "warning",
        );
        return;
      }
      // 1. Validar Datos Personales (solo en alta / edición regular)
      const personalForm = this.$refs.personalFormRef;
      if (
        !this.isExtension &&
        !this.isRenovation &&
        personalForm &&
        typeof personalForm.validateData === "function"
      ) {
        if (!personalForm.validateData()) {
          this.currentTab = "tab-personal";
          return;
        }
      }

      // 2. Validar Datos Laborales
      const laborForm = this.$refs.contractLaborRef;
      if (laborForm && typeof laborForm.validateData === "function") {
        if (!laborForm.validateData()) {
          this.currentTab = "tab-laboral";
          return;
        }
      }
      const labor = laborForm ? laborForm.getData() : null;
      if (!labor) {
        this.currentTab = "tab-laboral";
        return;
      }
      if (!labor.EstID) {
        labor.EstID = 1;
      }

      // 3. Validar Modalidad Formativa
      if (
        this.isModalidadFormativa &&
        !this.isExtension &&
        !this.isRenovation
      ) {
        const personalDataCheck = personalForm?.getContractData?.();
        const majors = personalDataCheck?.Majors || [];
        if (!majors.some((m) => m.SecStatus == 1)) {
          this.$fun.alert(
            "Para Modalidad Formativa debe registrar al menos una carrera académica en Datos Personales.",
            "warning",
          );
          this.currentTab = "tab-personal";
          if (personalForm) {
            personalForm.currentItem = "Education";
          }
          return;
        }
      }

      // 4. Validar SCTR si aplica
      let sctrData = null;
      if (this.isSCTR) {
        const sctrForm = this.$refs.contractSctrRef;
        if (sctrForm && typeof sctrForm.validateData === "function") {
          if (!sctrForm.validateData()) {
            this.currentTab = "tab-sctr";
            return;
          }
        }
        sctrData = sctrForm ? sctrForm.getData() : null;
        if (!sctrData) {
          this.$fun.alert(
            "Este contrato incluye SCTR. Por favor, complete la información en la pestaña de Datos Asegurados (SCTR).",
            "warning",
          );
          this.currentTab = "tab-sctr";
          return;
        }
      }

      // 5. Recolectar datos personales, cuentas y seguros
      const personalData = personalForm?.getContractData?.();
      const docNum =
        this.currentDocNumber ||
        this.editingContract?.PrsDocumentNumber ||
        this.contractFormData?.PrsDocumentNumber ||
        "";

      const personPayload = personalData?.Person || {
        PrsID: this.currentPrsID || 0,
        NtpID: this.currentNtpID || 0,
        PrsDocumentNumber: docNum,
      };
      if (personPayload && !personPayload.PrsDocumentNumber) {
        personPayload.PrsDocumentNumber = docNum;
      }

      // 6. Validar pertenencia del contrato al trabajador actual y determinar OperationType
      let activeEditingContract = this.editingContract;
      if (activeEditingContract && activeEditingContract.AgtID > 0) {
        if (
          activeEditingContract.WkrID &&
          this.currentWkrID &&
          activeEditingContract.WkrID !== this.currentWkrID
        ) {
          console.warn(
            "El contrato seleccionado no pertenece al trabajador actual. Creando como nuevo contrato.",
          );
          activeEditingContract = null;
          this.editingContract = null;
          this.selectedAgtID = null;
        }
      }

      const isCessation =
        labor.RegisterCessation ||
        labor.AgtStatus == 2 ||
        !!labor.AgtTerminationDate;

      let operationType = "NEW_CONTRACT";
      let subEvent = "ALTA";

      if (this.isExtension) {
        operationType = "EXTENSION";
        subEvent = "AMPLIACION";
      } else if (this.isRenovation) {
        operationType = "RENOVATION";
        subEvent = "RENOVACION";
      } else if (isCessation) {
        operationType = "CESSATION";
        subEvent = "BAJA";
      } else if (activeEditingContract && activeEditingContract.AgtID > 0) {
        operationType = "MODIFICATION";
        subEvent = labor.modificationScope || "MOD_LABORAL";
      }

      // 7. Estructurar Contrato, Seguros y Banco
      const contractPayload = {
        AgtID: this.isRenovation ? 0 : activeEditingContract?.AgtID || 0,
        AgtRenewedFromID: this.isRenovation
          ? activeEditingContract?.AgtID || null
          : null,
        WkrID: this.currentWkrID || 0,
        NtpID: this.currentNtpID || null,
        PrsID: this.currentPrsID || null,
        PrsDocumentNumber: docNum,
        ...labor,
        AgtRenovation: 0,
        AgtStatus: isCessation
          ? 2
          : this.isExtension ||
            (!this.isRenovation &&
              activeEditingContract &&
              activeEditingContract.AgtStatus == 1)
          ? 1
          : 0,
        AgtIsSCTR: this.isSCTR ? 1 : 0,
        AgtScrtBeginDate: sctrData?.SctrBeginDate || null,
        AgtScrtEndDate: sctrData?.SctrEndDate || null,
        ScpID: sctrData?.selectedScpID || null,
        SchID: sctrData?.selectedSchID || null,
        SctrPensionCE: sctrData?.SctrPension || [],
        SctrHealthCE: sctrData?.SctrHealth || [],
      };

      const fullPayload = {
        OperationType: operationType,
        SubEvent: subEvent,
        Person: personPayload,
        Contract: contractPayload,
      };

      const confirmMsg = isCessation
        ? `¿Confirma dar de baja al contrato con fecha de cese ${labor.AgtTerminationDate}?`
        : "¿Confirma guardar todos los datos del colaborador y contrato?";

      const confirm = await this.$fun.alert(confirmMsg, "question");
      if (!confirm.value) return;

      this.saving = true;
      try {
        const res = await _sCttoService.saveFull(
          fullPayload,
          this.$fun.getUserID(),
        );
        if (res?.status === 200 && res.data?.Success) {
          this.$fun.alert(
            res.data.Message || "Contrato y datos guardados exitosamente.",
            "success",
          );

          if (res.data.PrsID) this.currentPrsID = res.data.PrsID;
          if (res.data.NtpID) this.currentNtpID = res.data.NtpID;
          if (res.data.WkrID) this.currentWkrID = res.data.WkrID;
          if (res.data.AgtID) this.selectedAgtID = res.data.AgtID;

          this.closeContractForm();
          await this.loadContracts();
          this.currentTab = "tab-historial";
        } else {
          this.$fun.alert(
            res.data?.Message || "Ocurrió un error al guardar.",
            "error",
          );
        }
      } catch (e) {
        console.error("Error al guardar contrato:", e);
      } finally {
        this.saving = false;
      }
    },

    // ────────────────────────────────────────────────────────
    // HELPERS
    // ────────────────────────────────────────────────────────
    formatDate(d) {
      if (!d) return "—";
      return this.$fun.formatDateView
        ? this.$fun.formatDateView(d)
        : d.toString().substring(0, 10);
    },
    getStatusLabel(status) {
      const map = { 0: "Registrado", 1: "Activo", 2: "Cesado" };
      return map[status] !== undefined ? map[status] : "Registrado";
    },
    getStatusColor(status) {
      if (status == 0) return "amber lighten-4";
      if (status == 1) return "green lighten-4";
      if (status == 2) return "red lighten-4";
      return "blue lighten-4";
    },
    getStatusTextColor(status) {
      if (status == 0) return "amber darken-4";
      if (status == 1) return "green darken-3";
      if (status == 2) return "red darken-3";
      return "blue darken-3";
    },
  },
};
</script>

<style scoped>
.contract-hub {
  background: #f5f7fa;
  min-height: 100vh;
}

/* Timeline de contratos */
.contract-timeline-card {
  overflow: visible;
}

.contract-item {
  position: relative;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #ffffff;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.contract-item:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.contract-item--active {
  border-left: 3px solid #43a047 !important;
}

.contract-item--inactive {
  border-left: 3px solid #9e9e9e !important;
}

.contract-item--selected {
  border-color: #1565c0 !important;
  box-shadow: 0 3px 16px rgba(21, 101, 192, 0.2) !important;
}

.contract-header {
  border-radius: 10px;
}

.contract-header:hover {
  background: #f8f9fa;
}

.contract-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-active {
  background: #43a047;
  box-shadow: 0 0 0 3px rgba(67, 160, 71, 0.2);
}

.dot-inactive {
  background: #9e9e9e;
}

.movements-section {
  background: #fafafa;
  border-radius: 0 0 10px 10px;
}

.movement-item {
  padding: 4px 0;
}

.movement-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-mov-active {
  background: #66bb6a;
}

.dot-mov-inactive {
  background: #bdbdbd;
}

.timeline-line {
  position: absolute;
  left: 20px;
  bottom: -12px;
  width: 2px;
  height: 12px;
  background: #e0e0e0;
}
</style>
